// apps/spa/src/routes/CommandsList.tsx
// /commands — Slash command list page (CMD-01).
//
// Renders:
//   * page header 'Slash commands' (Fraunces) + '+ New command' button (top-right)
//   * Skeleton rows while useCommands().isLoading
//   * <CommandsEmptyState /> when commands.length === 0
//   * <CommandTable /> otherwise
//   * <DeleteCommandDialog /> behind a local state slot keyed by selected id

import { useState } from 'react';
import { Link } from 'react-router';
import { motion, useReducedMotion } from 'motion/react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useCommands } from '@/hooks/useCommands';
import { CommandTable } from '@/components/commands/CommandTable';
import { DeleteCommandDialog } from '@/components/commands/DeleteCommandDialog';
import { CommandsEmptyState } from '@/components/commands/EmptyState';
import { fadeUp } from '@/lib/motion';

export function CommandsList() {
  const commands = useCommands();
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const reduce = useReducedMotion();

  const deleteTarget =
    commands.data?.find((c) => c.id === deleteTargetId) ?? null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-widest text-primary/80">Configuration</p>
          <h1 className="font-display text-3xl">Slash commands</h1>
        </div>
        <Link to="/commands/new">
          <Button>+ New command</Button>
        </Link>
      </div>

      {commands.isLoading ? (
        <motion.div
          variants={fadeUp}
          initial={reduce ? false : 'hidden'}
          animate="show"
          className="glass p-2 sm:p-4 space-y-2"
        >
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </motion.div>
      ) : (commands.data?.length ?? 0) === 0 ? (
        <CommandsEmptyState />
      ) : (
        <motion.div
          variants={fadeUp}
          initial={reduce ? false : 'hidden'}
          animate="show"
          className="glass p-2 sm:p-4"
        >
          <CommandTable
            commands={commands.data ?? []}
            onDelete={setDeleteTargetId}
          />
        </motion.div>
      )}

      <DeleteCommandDialog
        command={deleteTarget}
        open={!!deleteTarget}
        onClose={() => setDeleteTargetId(null)}
      />
    </div>
  );
}
